/** Users · Recruitment Network (/app/network) — a node-graph view of one requisition's pipeline
 *  (Focused Network) or of every requisition (Constellation).
 *
 *  A SEPARATE view: the Dashboard at /app is untouched. To make this the home page later, point the
 *  `/app` index route in routes.tsx at <RecruitmentNetwork /> — nothing here assumes either way.
 *
 *  State split: server data comes from the same React Query keys RequisitionDetail/Dashboard use
 *  (so the caches are shared); the requisition lives in the URL (?req=) so a view is linkable;
 *  everything else — stage, mode, selection, page, dragged positions — is local UI state, scoped
 *  to the requisition+stage+mode it was made in so it resets itself without effects. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ZoomIn, ZoomOut, Maximize2, Network, RotateCcw, Activity, ChevronLeft, ChevronRight,
  SlidersHorizontal, ArrowUpDown, Check, Plus, Search, X, UserPlus, Compass, Briefcase, UploadCloud, MousePointerClick,
  FileSpreadsheet,
} from "lucide-react";
import { api } from "../../../lib/api.js";
import { useAuth } from "../../../lib/AuthContext.js";
import { RequirementCombobox } from "../../../components/RequirementCombobox.js";
import type { RequirementOption } from "../../../components/RequirementCombobox.js";
import { displayTitle } from "../displayTitle.js";
import { getRequisitionStatus } from "../requisitionFormat.js";
import type { Scan } from "../ResumeScoreList.js";
import { GraphCanvas } from "./GraphCanvas.js";
import type { GraphHandle } from "./GraphCanvas.js";
import {
  CandidateNode, ConstellationNode, EdgeLayer, HoverCard, HubNode, RequisitionNode, StageNode, StatusBadge,
  createHoverStore,
} from "./NetworkNodes.js";
import type { EdgeSpec } from "./NetworkNodes.js";
import { CandidateDetail } from "./CandidateDetail.js";
import { RequisitionPanel } from "./RequisitionPanel.js";
import type { FunnelRequisitionLite } from "./RequisitionPanel.js";
import { NetworkTour, networkTourSeen } from "./NetworkTour.js";
import { AddCandidateModal } from "./AddCandidateModal.js";
import { ImportCandidatesModal } from "../ImportCandidatesModal.js";
import {
  HUB_NODE, REQ_NODE, STAGES, candNodeId, candidatePageSize, computeConstellationLayout, computeFocusedLayout,
  STATUS_META, edgePath, inStage, matchesSearch, mergeCandidates, rankForStage, SORTS, sortCandidates, reqNodeId, spokePath, stageCounts, stageNodeId,
} from "./networkModel.js";
import type {
  CandidateStatus, FunnelCandidateLite, LayoutNode, NetRequisition, SortKey, StageKey, StatusFilter,
} from "./networkModel.js";
import "../../../styles/network.css";

type Mode = "focused" | "constellation";

/* The requirement the recruiter last picked, remembered per user so a refresh (or coming back
   later) stays on it instead of snapping to the newest one. localStorage can be missing or throw
   (private window, blocked site data) — every access is guarded and the page works without it. */
const savedReqKey = (userId: string): string => `th.network.req.${userId}`;
function readSavedReq(userId: string): string | null {
  if (!userId) return null;
  try {
    return window.localStorage.getItem(savedReqKey(userId));
  } catch {
    return null;
  }
}
function saveReq(userId: string, id: string): void {
  if (!userId || !id) return;
  try {
    window.localStorage.setItem(savedReqKey(userId), id);
  } catch {
    /* remembering is a convenience only */
  }
}

export function RecruitmentNetwork(): ReactNode {
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState<Mode>("focused");
  const [stage, setStage] = useState<StageKey>("scanning");
  const [flowing, setFlowing] = useState(true);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [resetNonce, setResetNonce] = useState(0);
  const graph = useRef<GraphHandle>(null);
  const hover = useMemo(() => createHoverStore(), []);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [sort, setSort] = useState<SortKey>("best");
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  /** Resumes dropped on the page while no modal was open — handed to the Add candidate modal. */
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [pageDrag, setPageDrag] = useState(false);

  // ------------------------------------------------------------ first-run walkthrough
  // Opens once per user on first visit; the profile menu's "Getting started guide" links here with
  // ?tour=1 to reopen it. The flag is consumed so a reload doesn't reopen the tour.
  const { me } = useAuth();
  const userId = me?.userId ?? "";
  const [tourOpen, setTourOpen] = useState(false);
  const wantsTour = params.get("tour") === "1";
  useEffect(() => {
    if (!userId) return;
    if (wantsTour) {
      setTourOpen(true);
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          n.delete("tour");
          return n;
        },
        { replace: true },
      );
    }
  }, [userId, wantsTour, setParams]);

  // ------------------------------------------------------------ server data (shared caches)
  const reqsQ = useQuery({
    queryKey: ["requisitions"],
    queryFn: () => api.get<{ requisitions: NetRequisition[] }>("/requisitions"),
  });
  const reqs = useMemo(() => reqsQ.data?.requisitions ?? [], [reqsQ.data]);
  // The tour only auto-opens once the org HAS a requirement — with none, the empty state below (and
  // the classic dashboard's Get started checklist) leads instead. ?tour=1 still opens it any time.
  const hasAnyReq = reqs.length > 0;
  useEffect(() => {
    if (!userId || wantsTour || !reqsQ.isSuccess || !hasAnyReq) return;
    if (!networkTourSeen(userId)) setTourOpen(true);
  }, [userId, wantsTour, reqsQ.isSuccess, hasAnyReq]);
  // The selector offers ACTIVE requirements only (no draft / on-hold / closed), newest first.
  const activeReqs = useMemo(
    () =>
      reqs
        .filter((r) => getRequisitionStatus(r.state) === "Active")
        // Newest requirement first; resume / interview counts only break an exact tie.
        .sort(
          (a, b) =>
            b.createdAt.localeCompare(a.createdAt) ||
            (b.scannedCount ?? 0) - (a.scannedCount ?? 0) ||
            (b.interviewCount ?? 0) - (a.interviewCount ?? 0),
        ),
    [reqs],
  );
  const reqOptions: RequirementOption[] = useMemo(
    () =>
      activeReqs.map((r) => ({
        id: r.id,
        title: displayTitle(r.title),
        meta: `${r.scannedCount ?? 0} resumes · ${r.interviewCount ?? 0} interviews${r.config.location ? ` · ${r.config.location}` : ""}`,
      })),
    [activeReqs],
  );
  // URL first (a shared / bookmarked link wins), then the remembered pick if it is still an active
  // requirement, then the newest.
  const urlReq = params.get("req");
  const savedReq = readSavedReq(userId);
  const reqId =
    urlReq ?? (savedReq && activeReqs.some((r) => r.id === savedReq) ? savedReq : null) ?? activeReqs[0]?.id ?? reqs[0]?.id ?? "";
  // An explicit ?req= (picked here, or arriving from a link) becomes the remembered one.
  useEffect(() => {
    if (urlReq && reqs.some((r) => r.id === urlReq)) saveReq(userId, urlReq);
  }, [userId, urlReq, reqs]);
  const req = reqs.find((r) => r.id === reqId);

  const scansQ = useQuery({
    queryKey: ["reqScans", reqId],
    queryFn: () => api.get<{ scans: Scan[] }>(`/requisitions/${reqId}/resumes`),
    enabled: !!reqId && mode === "focused",
  });
  const funnelQ = useQuery({
    queryKey: ["funnel", reqId],
    queryFn: () =>
      api.get<{ candidates: FunnelCandidateLite[]; requisition?: FunnelRequisitionLite }>(`/requisitions/${reqId}/funnel`),
    enabled: !!reqId && mode === "focused",
  });

  const all = useMemo(
    () => mergeCandidates(req, scansQ.data?.scans ?? [], funnelQ.data?.candidates ?? []),
    [req, scansQ.data, funnelQ.data],
  );
  const byId = useMemo(() => new Map(all.map((c) => [c.id, c])), [all]);
  const counts = useMemo(() => stageCounts(all), [all]);
  const ranked = useMemo(() => rankForStage(all.filter((c) => inStage(c, stage)), stage), [all, stage]);
  const statusCounts = useMemo(() => {
    const n: Record<StatusFilter, number> = { all: ranked.length, progressing: 0, rejected: 0, interviewed: 0, pending: 0 };
    for (const c of ranked) n[c.status]++;
    return n;
  }, [ranked]);
  // The filter and search narrow what is DRAWN; ranks stay the candidate's rank within the whole stage.
  const shown = useMemo(
    () => sortCandidates(ranked.filter((c) => (filter === "all" || c.status === filter) && matchesSearch(c, query)), sort),
    [ranked, filter, query, sort],
  );
  // Where else the search finds people — so "no match here" can point at the stage they're in.
  const searchHits = useMemo(() => {
    if (!query.trim()) return null;
    const hits = all.filter((c) => matchesSearch(c, query));
    return STAGES.map((s) => ({ ...s, n: hits.filter((c) => inStage(c, s.key)).length })).filter((s) => s.n > 0);
  }, [all, query]);

  // ------------------------------------------------------------ scoped UI state
  const scope = `${mode}|${reqId}|${stage}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const [sel, setSel] = useState<{ scope: string; id: string } | null>(null);
  const selectedId = sel && sel.scope.split("|").slice(1).join("|") === `${reqId}|${stage}` ? sel.id : null;
  const selected = selectedId ? byId.get(selectedId) : undefined;
  // The requirement's own detail panel — shares the side slot with the candidate panel, so opening
  // one closes the other. Keyed to the requirement so switching requirement closes it.
  const [reqPanelFor, setReqPanelFor] = useState<string | null>(null);
  const reqPanelOpen = reqPanelFor === reqId && !selected;
  const selectCandidate = useCallback((id: string) => {
    setReqPanelFor(null);
    setSel({ scope: scopeRef.current, id });
  }, []);
  const clearSelection = useCallback(() => {
    setSel(null);
    setReqPanelFor(null);
  }, []);
  const reqIdRef = useRef(reqId);
  reqIdRef.current = reqId;
  const openReqPanel = useCallback(() => {
    hover.set(null);
    setSel(null);
    setReqPanelFor(reqIdRef.current);
  }, [hover]);

  const pageScope = `${scope}|${filter}|${sort}|${query.trim().toLowerCase()}`;
  const [pageState, setPageState] = useState<{ scope: string; page: number }>({ scope: pageScope, page: 0 });
  const pageSize = candidatePageSize(size.h || 700);
  const pageCount = Math.max(1, Math.ceil(shown.length / pageSize));
  const page = pageState.scope === pageScope ? Math.min(pageState.page, pageCount - 1) : 0;
  const visible = useMemo(() => shown.slice(page * pageSize, (page + 1) * pageSize), [shown, page, pageSize]);
  const goPage = (p: number): void => {
    hover.set(null);
    setPageState({ scope: pageScope, page: p });
  };

  const [moved, setMoved] = useState<{ scope: string; pos: Record<string, { x: number; y: number }> }>({
    scope: "",
    pos: {},
  });
  const movedKey = `${pageScope}|${page}|${resetNonce}`;
  const overrides = moved.scope === movedKey ? moved.pos : undefined;
  const movedKeyRef = useRef(movedKey);
  movedKeyRef.current = movedKey;
  const onNodeMove = useCallback((id: string, x: number, y: number) => {
    setMoved((m) => {
      const base = m.scope === movedKeyRef.current ? m.pos : {};
      return { scope: movedKeyRef.current, pos: { ...base, [id]: { x, y } } };
    });
  }, []);

  const onSize = useCallback((w: number, h: number) => setSize({ w, h }), []);
  const selectStage = useCallback(
    (s: StageKey) => {
      hover.set(null); // the hovered card is about to unmount without a mouseleave
      setStage(s);
    },
    [hover],
  );
  const openReq = useCallback(
    (id: string) => {
      hover.set(null);
      saveReq(userId, id);
      setParams((p) => {
        const n = new URLSearchParams(p);
        n.set("req", id);
        return n;
      });
      setMode("focused");
    },
    [setParams, hover, userId],
  );

  // ------------------------------------------------------------ layout (never on hover)
  const visibleIds = visible.map((c) => c.id).join(",");
  const focused = useMemo(
    () => computeFocusedLayout({ viewportW: size.w || 1200, viewportH: size.h || 700, candidateIds: visibleIds ? visibleIds.split(",") : [] }),
    [size.w, size.h, visibleIds],
  );
  const reqOrder = useMemo(
    () =>
      [...reqs]
        .sort((a, b) => {
          const act = (r: NetRequisition): number => (getRequisitionStatus(r.state) === "Active" ? 1 : 0);
          return act(b) - act(a) || (b.scannedCount ?? 0) - (a.scannedCount ?? 0);
        })
        .map((r) => r.id),
    [reqs],
  );
  const constellation = useMemo(() => computeConstellationLayout(reqOrder), [reqOrder]);

  const nodes: LayoutNode[] = useMemo(() => {
    const base = mode === "focused" ? focused.nodes : constellation;
    return overrides ? base.map((n) => (overrides[n.id] ? { ...n, ...overrides[n.id] } : n)) : base;
  }, [mode, focused, constellation, overrides]);
  const box = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  const edges: EdgeSpec[] = useMemo(() => {
    const out: EdgeSpec[] = [];
    const link = (a: string, b: string, tone: EdgeSpec["tone"], spoke = false): void => {
      const A = box.get(a);
      const B = box.get(b);
      if (A && B) out.push({ id: `${a}>${b}`, d: spoke ? spokePath(A, B) : edgePath(A, B), tone });
    };
    if (mode === "constellation") {
      for (const id of reqOrder) link(HUB_NODE, reqNodeId(id), id === reqId ? "selected" : "spoke", true);
      return out;
    }
    for (const s of STAGES) link(REQ_NODE, stageNodeId(s.key), s.key === stage ? "active" : "idle");
    const [left, right] = focused.columns;
    left.forEach((id, i) => {
      const mate = right[i];
      const selHere = selectedId === id || (mate != null && selectedId === mate);
      link(stageNodeId(stage), candNodeId(id), selHere ? "selected" : "active");
      if (mate) link(candNodeId(id), candNodeId(mate), selectedId === mate ? "selected" : "idle");
    });
    return out;
  }, [box, mode, reqOrder, reqId, stage, focused.columns, selectedId]);

  // ------------------------------------------------------------ world content
  const world =
    mode === "focused" ? (
      req ? (
        <>
          <EdgeLayer edges={edges} flowing={flowing} />
          <RequisitionNode
            box={box.get(REQ_NODE)!}
            req={req}
            counts={{
              resumes: req.scannedCount ?? counts.received,
              matches: req.matchesCount ?? 0,
              interviews: req.interviewCount ?? counts.completed,
            }}
            open={reqPanelOpen}
            onOpen={openReqPanel}
          />
          {STAGES.map((s) => (
            <StageNode
              key={s.key}
              box={box.get(stageNodeId(s.key))!}
              stage={s.key}
              label={s.label}
              hint={s.hint}
              count={counts[s.key]}
              active={s.key === stage}
              onSelect={selectStage}
            />
          ))}
          {visible.map((c) => (
            <CandidateNode
              key={c.id}
              box={box.get(candNodeId(c.id))!}
              c={c}
              stage={stage}
              rank={ranked.indexOf(c) + 1}
              selected={c.id === selectedId}
              hover={hover}
              onSelect={selectCandidate}
            />
          ))}
        </>
      ) : null
    ) : (
      <>
        <EdgeLayer edges={edges} flowing={flowing} />
        <HubNode
          box={box.get(HUB_NODE)!}
          total={reqs.length}
          active={reqs.filter((r) => getRequisitionStatus(r.state) === "Active").length}
        />
        {reqs.map((r) => (
          <ConstellationNode key={r.id} box={box.get(reqNodeId(r.id))!} req={r} focused={r.id === reqId} onOpen={openReq} />
        ))}
      </>
    );

  const loadingCands = mode === "focused" && (scansQ.isLoading || funnelQ.isLoading);
  const fitKey = `${pageScope}|${page}|${resetNonce}|${visible.length}|${reqs.length}`;
  const stageLabel = STAGES.find((s) => s.key === stage)!.label;

  // ------------------------------------------------------------ controls (screen space)
  // ONE toolbar row across the top. The requirement picker and the search are the elastic parts;
  // everything else is fixed-width. As the row narrows (a container query on .rn-controls, so an
  // open detail panel counts too) labels drop to icons in tiers — `lbl-1` first, then `lbl-2` —
  // and on phone widths the view-only tools (zoom, reset, live) hide: pinch and drag still work.
  const onFocused = mode === "focused";

  // Drop resumes anywhere on the page: opens Add candidate with them already chosen. (While that modal
  // is open it handles drops itself.) Needs one requirement in focus — files score against a requirement.
  const canDrop = onFocused && !!req && !addOpen && !importOpen && !tourOpen;
  useEffect(() => {
    if (!canDrop) return;
    let depth = 0;
    const hasFiles = (e: DragEvent): boolean => Array.from(e.dataTransfer?.types ?? []).includes("Files");
    const enter = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth++;
      setPageDrag(true);
    };
    const over = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };
    const leave = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setPageDrag(false);
    };
    const drop = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setPageDrag(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (!files.length) return;
      setDroppedFiles(files);
      setAddOpen(true);
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragover", over);
    window.addEventListener("dragleave", leave);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragover", over);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("drop", drop);
      setPageDrag(false);
    };
  }, [canDrop]);
  const overlay = (
    <>
      <div className="rn-controls" data-no-pan>
        <div className="rn-toolbar rn-bar" role="toolbar" aria-label="Network controls">
          <RequirementCombobox
            className="rn-combo"
            options={reqOptions}
            value={activeReqs.some((r) => r.id === reqId) ? reqId : ""}
            onSelect={openReq}
          />
          <button
            type="button"
            className="rn-tool wide rn-primary"
            disabled={!req}
            onClick={() => setAddOpen(true)}
            data-tip="Upload CVs or invite by email"
            aria-label="Add candidate"
          >
            <UserPlus size={15} /> <span className="lbl-keep">Add candidate</span>
          </button>
          <button
            type="button"
            className="rn-tool rn-labelled"
            disabled={!req}
            onClick={() => setImportOpen(true)}
            data-tip="Import many candidates from Excel / CSV"
            aria-label="Bulk import candidates from Excel"
          >
            <FileSpreadsheet size={15} /> <span className="lbl-keep">Bulk import</span>
          </button>
          <span className="rn-tool-sep" />
          <label className={`rn-search${onFocused ? "" : " is-disabled"}`}>
            <Search size={14} aria-hidden="true" />
            <input
              type="search"
              value={query}
              disabled={!onFocused}
              placeholder="Search candidates"
              aria-label="Search candidates by name, email, skill or CV file"
              onChange={(e) => {
                hover.set(null);
                setQuery(e.target.value);
              }}
              onKeyDown={(e) => e.key === "Escape" && setQuery("")}
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="Clear search" data-tip="Clear search">
                <X size={13} />
              </button>
            )}
          </label>
          <SortMenu value={sort} disabled={!onFocused} onChange={(k) => (hover.set(null), setSort(k))} />
          <FilterMenu value={filter} counts={statusCounts} disabled={!onFocused} onChange={(f) => (hover.set(null), setFilter(f))} />
          <label className="t1-switch rn-switch" data-tip={onFocused ? "Show every requirement (Constellation)" : "Back to one requirement (Focused)"}>
            <input
              type="checkbox"
              checked={!onFocused}
              onChange={(e) => setMode(e.target.checked ? "constellation" : "focused")}
              aria-label="Constellation view — every requirement at once"
            />
            <span className="track" />
            <span className="rn-switch-lbl">Constellation</span>
          </label>
          {onFocused && (
            <div className="rn-pager">
              <button type="button" disabled={page === 0} onClick={() => goPage(page - 1)} aria-label="Previous candidates" data-tip="Previous candidates">
                <ChevronLeft size={15} />
              </button>
              <span className="tnum">
                {shown.length ? `${page * pageSize + 1}–${Math.min(shown.length, (page + 1) * pageSize)} of ${shown.length}` : "0 of 0"}
              </span>
              <button type="button" disabled={page >= pageCount - 1} onClick={() => goPage(page + 1)} aria-label="Next candidates" data-tip="Next candidates">
                <ChevronRight size={15} />
              </button>
            </div>
          )}

        </div>
      </div>

      {/* view-only tools live bottom-right, away from the working toolbar */}
      <div className="rn-zoom-dock" role="toolbar" aria-label="View controls" data-no-pan>
        <button type="button" className="rn-tool" onClick={() => graph.current?.zoomBy(1.2)} aria-label="Zoom in" data-tip="Zoom in" data-tip-pos="top">
          <ZoomIn size={16} />
        </button>
        <button type="button" className="rn-tool" onClick={() => graph.current?.zoomBy(1 / 1.2)} aria-label="Zoom out" data-tip="Zoom out" data-tip-pos="top">
          <ZoomOut size={16} />
        </button>
        <button type="button" className="rn-tool" onClick={() => graph.current?.fit()} aria-label="Fit to screen" data-tip="Fit to screen" data-tip-pos="top">
          <Maximize2 size={15} />
        </button>
        <button
          type="button"
          className="rn-tool"
          onClick={() => {
            setMoved({ scope: "", pos: {} });
            setResetNonce((n) => n + 1);
          }}
          aria-label="Reset layout"
          data-tip="Reset layout"
          data-tip-pos="top"
        >
          <RotateCcw size={15} />
        </button>
        <button
          type="button"
          className={`rn-tool${flowing ? " is-live" : ""}`}
          onClick={() => setFlowing((f) => !f)}
          aria-pressed={flowing}
          aria-label="Live streams"
          data-tip={flowing ? "Pause live streams" : "Animate live streams"}
          data-tip-pos="top"
        >
          <Activity size={15} />
        </button>
      </div>

      {mode === "focused" && req && !loadingCands && shown.length === 0 && (
        <div className="rn-empty-stage" data-no-pan>
          {query.trim() ? (
            <>
              No candidates matching <b>“{query.trim()}”</b> at <b>{stageLabel}</b>
              {filter !== "all" ? <> with status <b>{STATUS_META[filter].label.toLowerCase()}</b></> : null}.{" "}
              {searchHits?.filter((h) => h.key !== stage).map((h) => (
                <button key={h.key} type="button" className="rn-link" onClick={() => selectStage(h.key)} style={{ marginRight: 8 }}>
                  {h.n} in {h.label}
                </button>
              ))}
              <button type="button" className="rn-link" onClick={() => setQuery("")}>Clear search</button>
            </>
          ) : ranked.length === 0 || filter === "all" ? (
            <>No candidates at <b>{stageLabel}</b> yet.</>
          ) : (
            <>
              No <b>{STATUS_META[filter].label.toLowerCase()}</b> candidates at <b>{stageLabel}</b>.{" "}
              <button type="button" className="rn-link" onClick={() => setFilter("all")}>Show all</button>
            </>
          )}
        </div>
      )}

      <div className="rn-legend" data-no-pan>
        {mode === "focused" ? (
          (Object.keys(STATUS_META) as CandidateStatus[]).map((s) => (
            <span key={s}><StatusBadge status={s} /> {STATUS_META[s].label}</span>
          ))
        ) : (
          <>
            <span><i className="c-green" /> Active</span>
            <span><i className="c-amber" /> On hold</span>
            <span><i className="c-mute" /> Draft / closed</span>
          </>
        )}
      </div>

    </>
  );

  return (
    <div className="rn">
      <div className="rn-body">
        {reqsQ.isLoading ? (
          <div className="rn-state">Loading your requirements…</div>
        ) : reqsQ.isError ? (
          <div className="rn-state">Couldn’t load requirements. Refresh to try again.</div>
        ) : reqs.length === 0 ? (
          <NoRequirements onTour={() => setTourOpen(true)} />
        ) : (
          <GraphCanvas
            ref={graph}
            nodes={nodes}
            fitKey={fitKey}
            minFitZoom={mode === "focused" ? 0.6 : 0.3}
            // +18 over the toolbar band: the "#17" rank badge sits ABOVE its card, outside the
            // node bounds the fit measures, and was tucking under the toolbar.
            insetTop={size.w && size.w <= 640 ? 94 : 48}
            onSize={onSize}
            onNodeMove={onNodeMove}
            onBackgroundClick={clearSelection}
            onViewportInteract={() => hover.set(null)}
            overlay={overlay}
          >
            {world}
          </GraphCanvas>
        )}

        {/* Only once a candidate is chosen — until then the canvas gets the full width. */}
        {mode === "focused" && req && selected && (
          <CandidateDetail key={selected.id} c={selected} req={req} onClose={clearSelection} />
        )}
        {mode === "focused" && req && reqPanelOpen && (
          <RequisitionPanel
            key={req.id}
            req={req}
            detail={funnelQ.data?.requisition}
            loading={funnelQ.isLoading}
            onClose={clearSelection}
          />
        )}
      </div>

      <HoverCard store={hover} byId={byId} stage={stage} />

      {pageDrag && canDrop && (
        <div className="rn-dropveil" aria-hidden="true">
          <UploadCloud size={44} />
          <b>Drop resumes to add them to {displayTitle(req!.title)}</b>
          <span>PDF, Word or text · up to 20 at a time</span>
        </div>
      )}
      {addOpen && req && (
        <AddCandidateModal
          reqId={req.id}
          reqTitle={req.title}
          initialFiles={droppedFiles}
          onClose={() => (setAddOpen(false), setDroppedFiles([]))}
        />
      )}
      {importOpen && req && <ImportCandidatesModal reqId={req.id} reqTitle={req.title} onClose={() => setImportOpen(false)} />}

      {tourOpen && userId && (
        <NetworkTour userId={userId} hasRequirements={reqs.length > 0} onClose={() => setTourOpen(false)} />
      )}
    </div>
  );
}

/** Zero requirements: the network has nothing to draw, so say what it will show and how to start. */
function NoRequirements({ onTour }: { onTour: () => void }): ReactNode {
  const steps = [
    { icon: <Briefcase size={18} />, title: "Create a requirement", text: "Describe the role — we build the skills and interview plan from it." },
    { icon: <UploadCloud size={18} />, title: "Add candidates", text: "Upload CVs, add people directly, or forward applications by email." },
    { icon: <MousePointerClick size={18} />, title: "Track the pipeline", text: "Watch each candidate move from resume scan to completed interview." },
  ];
  return (
    <div className="rn-empty" data-no-pan>
      <div className="rn-empty-icon"><Network size={26} /></div>
      <h2 className="rn-empty-title">Your recruitment network starts with a requirement</h2>
      <p className="rn-empty-sub">
        You don’t have any requirements yet. Once you create one, its candidates and pipeline stages appear here as a
        live map.
      </p>
      <ol className="rn-empty-steps">
        {steps.map((s, i) => (
          <li key={s.title}>
            <span className="rn-empty-step-icon">{s.icon}</span>
            <span>
              <b>{i + 1}. {s.title}</b>
              <span className="rn-muted sm">{s.text}</span>
            </span>
          </li>
        ))}
      </ol>
      <div className="rn-empty-actions">
        <Link className="rn-tour-primary" to="/app/new">
          <Plus size={15} /> Create your first requirement
        </Link>
        <button type="button" className="rn-tool wide" onClick={onTour}>
          <Compass size={15} /> Take the tour
        </button>
      </div>
    </div>
  );
}

const FILTERS: StatusFilter[] = ["all", "progressing", "rejected", "interviewed", "pending"];

/** Toolbar filter: which candidate statuses the graph draws. Counts are for the active stage. */
function FilterMenu({
  value,
  counts,
  disabled,
  onChange,
}: {
  value: StatusFilter;
  counts: Record<StatusFilter, number>;
  disabled: boolean;
  onChange: (f: StatusFilter) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <div className="rn-filter" ref={ref}>
      <button
        type="button"
        className={`rn-tool${value !== "all" ? " is-on has-dot" : ""}`}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-tip={open ? undefined : value === "all" ? "Filters" : `Showing ${STATUS_META[value].label.toLowerCase()} only`}
        aria-label={value === "all" ? "Filters" : `Filters: ${STATUS_META[value].label}`}
      >
        <SlidersHorizontal size={15} />
      </button>
      {open && (
        <div className="rn-menu" role="menu">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              role="menuitemradio"
              aria-checked={value === f}
              className={value === f ? "is-on" : ""}
              onClick={() => {
                onChange(f);
                setOpen(false);
              }}
            >
              <span className="rn-menu-check">{value === f && <Check size={14} />}</span>
              {f === "all" ? <span className="rn-menu-all" /> : <StatusBadge status={f} />}
              <span className="rn-menu-label">{f === "all" ? "All candidates" : STATUS_META[f].label}</span>
              <span className="rn-menu-count tnum">{counts[f]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Toolbar sort: reorders the cards on the graph (the rank badge keeps the "best match" position). */
function SortMenu({
  value,
  disabled,
  onChange,
}: {
  value: SortKey;
  disabled: boolean;
  onChange: (k: SortKey) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  const label = SORTS.find((s) => s.key === value)?.label ?? "";

  return (
    <div className="rn-filter" ref={ref}>
      <button
        type="button"
        className={`rn-tool${value !== "best" ? " is-on has-dot" : ""}`}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-tip={open ? undefined : value === "best" ? "Sort" : `Sorted: ${label}`}
        aria-label={value === "best" ? "Sort candidates" : `Sort: ${label}`}
      >
        <ArrowUpDown size={15} />
      </button>
      {open && (
        <div className="rn-menu" role="menu">
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              role="menuitemradio"
              aria-checked={value === s.key}
              className={value === s.key ? "is-on" : ""}
              onClick={() => {
                onChange(s.key);
                setOpen(false);
              }}
            >
              <span className="rn-menu-check">{value === s.key && <Check size={14} />}</span>
              <span className="rn-menu-label">{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
